import { motion, useSpring } from "framer-motion";
import styled from "@emotion/styled";
import { container } from "tsyringe";
import { DomainStore } from "Stores/Domain/DomainStore";
import { Logger } from "index";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { HeaderLogoOptionBase } from "./HeaderLogoOptions";
import { IHeaderStyle } from "./HeaderBase";
import classNames from "classnames";

const domainStore = container.resolve(DomainStore);

const Container = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    touch-action: none;
    z-index: 3;
`;

const SidebarContainer = styled.div<{ width: number }>`
    position: fixed;
    background-color: ${({ color }) => `${color}`};
    left: ${({ width }: { width: number }) => `${-width}px`};
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    padding: 64px 0 0 0;

    &.is-open {
        left: 0px;
        width: ${({ width }: { width: number }) => `${width}px`};
        transition: left 0.2s ease-in-out;
    }
`;

const HamburgerContainer = styled(motion.div)`
    background-color: ${({ color }) => `${color}`};

    position: absolute;
    width: 48px;
    height: 48px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 3;
    pointer-events: all;
`;

const Line = styled(motion.div)`
    background-color: #000;
    width: ${({ width }: { width: any }) => `${width}px`};
    height: 2px;
    margin: 2px 14px;
    border-radius: 8px;
`;

const HamburgerButton = observer(({ x, width, color }: { x: number; width: number; color: string }) => {
    reaction(
        () => domainStore.isSideBarClosed,
        (sidebarOpen) => {
            domainStore.isSideBarClosed ? (x as any).set(-width) : (x as any).set(0);
        },
    );

    return (
        <HamburgerContainer
            id={"hamburger"}
            onTap={() => {
                Logger.logDebug("Sidebar is open ", domainStore.isSideBarClosed);
                domainStore.setSideBarClosed(!domainStore.isSideBarClosed);
            }}
        >
            <Line
                width={16}
                animate={{ rotate: domainStore.isSideBarClosed ? 0 : "-45deg", width: domainStore.isSideBarClosed ? "16px" : "14px" }}
                transition={{ duration: 0.2 }}
            />
            <Line width={16} animate={{ width: domainStore.isSideBarClosed ? "16px" : "20px" }} transition={{ duration: 0.2 }} />
            <Line width={16} animate={{ rotate: domainStore.isSideBarClosed ? 0 : "45deg", width: domainStore.isSideBarClosed ? "16px" : "14px" }} transition={{ duration: 0.2 }} />
        </HamburgerContainer>
    );
});

export const Sidebar = observer(({ width, color = "#1c2022", children, left = true, props }: { width: number; color: string; children: any; left: boolean; props: any }) => {
    const x = useSpring(0, { stiffness: 400, damping: 40 });
    const { styles } = props;
    const headerStyleHandler = (props: IHeaderStyle) => {
        return {
            styles: styles ? props.CSSProperties : undefined,
            className: styles ? props.className : undefined,
        };
    };

    const logoOptionStyles = styles ? headerStyleHandler(styles.logoOption).styles : undefined;
    const logoOptionClassName = styles ? headerStyleHandler(styles.logoOption).className : undefined;
    return (
        <Container id={"sidebar"} style={{ pointerEvents: domainStore.isSideBarClosed ? "none" : "all" }}>
            <HeaderLogoOptionBase style={logoOptionStyles} className={logoOptionClassName} />
            <HamburgerButton x={x as any} width={width} color={color} />
            <SidebarContainer color={color} width={width} className={classNames({ "is-open": !domainStore.isSideBarClosed })}>
                {children}
            </SidebarContainer>
        </Container>
    );
});
